import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BlindKingsGame,
  DIRECTIONS,
  GAME_ACTIONS,
  GAME_MODES,
  type GamePiece,
  type LastAction,
} from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useSnackBarContext } from '../../../contexts/SnackBarContext';
import useApiAction from '../../../hooks/use-api-action';
import useBoardOrientation from '../../../hooks/use-board-orientation';
import { playerIsInGameId } from '../../../utils/active-games';
import useOptimisticGameActions from './use-optimistic-game-actions';
import useBotOpponent from './use-bot-opponent';

export type GameRecord = Record<string, unknown> & {
  gameId?: string;
  gameMode?: string;
  botDifficulty?: string;
  firstPlayerRewards?: unknown;
  secondPlayerRewards?: unknown;
  lastFocus?: {
    id: string;
    from: { x: number; y: number };
    to: { x: number; y: number };
  };
  lastAction?: LastAction;
  encodedGameState?: string;
  board?: unknown;
  graveyard?: unknown;
  following?: unknown;
};

export type UseGameStateOptions = {
  game: GameRecord;
  applyGameUpdate?: (payload: GameRecord) => void;
  skinId?: string;
};

export default function useGameState({
  game,
  applyGameUpdate,
  skinId = 'classic',
}: UseGameStateOptions) {
  const {
    userData,
    queryUserData,
  } = useAuthContext();
  const { playerId } = userData ?? {};
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [showLeaveGameModal, setShowLeaveGameModal] = useState(false);
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const { renderVertically } = useBoardOrientation(boardAreaRef);
  const { addSnackBar } = useSnackBarContext();

  const serverGame = useMemo(
    () =>
      new BlindKingsGame({
        ...(game as Record<string, unknown>),
        lastAction: game.lastAction,
        userPlayerId: playerId,
      }),
    [game, playerId]
  );

  const {
    displayGame,
    canvasBoard,
    pendingFlipPieceId,
    playRequest,
    remoteFocus,
    inputLocked,
    onPlayComplete,
    dispatchMoveOrAttack,
    dispatchFlip,
    clearActionIndicators,
  } = useOptimisticGameActions({
    gameId,
    serverGame,
    applyGameUpdate,
    playerId,
    lastFocus: game.lastFocus,
    lastAction: game.lastAction,
  });

  useBotOpponent({
    game,
    playerId,
    inputLocked,
    applyGameUpdate,
    onError: (message) => {
      addSnackBar({
        title: 'Bot move failed',
        message,
        variant: 'danger',
        timeout: 3000,
      });
    },
  });

  const rewards = useMemo(() => {
    if (serverGame.userIsFirstPlayer) return game.firstPlayerRewards;
    if (serverGame.userIsSecondPlayer) return game.secondPlayerRewards;
  }, [serverGame, game]);

  const [leaveGame, leaving] = useApiAction({
    action: async () => api.leaveGame(String(gameId)),
    reloadUserData: true,
  });
  const [leaveGameQuiet] = useApiAction({
    action: async () => api.leaveGame(String(gameId)),
    reloadUserData: true,
    quietly: true,
  });

  const canReset =
    serverGame.gameMode === GAME_MODES.LOCAL_GAME ||
    serverGame.gameMode === GAME_MODES.AI_GAME;

  const [resetStarted, setResetStarted] = useState(false);
  const [createResetGame, creatingReset] = useApiAction({
    action: async () => {
      const mode = String(serverGame.gameMode);
      const options =
        mode === GAME_MODES.AI_GAME
          ? { botDifficulty: String(game.botDifficulty ?? 'hard') }
          : {};
      return api.createGame(mode, options);
    },
    reloadUserData: true,
  });
  const resetting = resetStarted || creatingReset;

  const [selectedPieceId, setSelectedPieceId] = useState<number | undefined>();

  useEffect(() => {
    if (!displayGame.isUserTurn || displayGame.gameWinner || inputLocked) {
      setSelectedPieceId(undefined);
    }
  }, [displayGame.isUserTurn, displayGame.gameWinner, inputLocked]);

  const {
    gameBoard: { allPieces = [] } = {},
  } = displayGame;

  const selectedPiece = useMemo(
    () => allPieces.find(({ id }) => id === selectedPieceId),
    [selectedPieceId, allPieces]
  );

  const selectPiece = useCallback(
    (gamePiece: GamePiece) => {
      const { isUserTurn, turnIndex, gameWinner, gameBoard } = displayGame;

      if (!isUserTurn || gameWinner || inputLocked) return;

      if (selectedPiece) {
        const direction = gameBoard.getDirectionBetweenLocations(
          selectedPiece.location,
          gamePiece.location
        );

        if (direction === DIRECTIONS.CENTER) return setSelectedPieceId(undefined);
        if (gamePiece.teamIndex === selectedPiece.teamIndex) {
          return setSelectedPieceId(gamePiece.id);
        }

        const moveTarget = selectedPiece.getPieceToMoveToByDirection(direction);
        const canMoveToPiece =
          selectedPiece.canMoveByDirection(direction) &&
          !!moveTarget &&
          (moveTarget === gamePiece ||
            (moveTarget.id === gamePiece.id &&
              moveTarget.location.x === gamePiece.location.x &&
              moveTarget.location.y === gamePiece.location.y));

        if (canMoveToPiece) {
          void dispatchMoveOrAttack({
            action: GAME_ACTIONS.MOVE,
            pieceId: selectedPiece.id,
            direction,
            gamePiece: {
              id: selectedPiece.id,
              value: selectedPiece.value,
              teamIndex: selectedPiece.teamIndex,
              location: { ...selectedPiece.location },
            },
          });
          setSelectedPieceId(undefined);
          return;
        }

        const attackTarget = selectedPiece.getPieceToAttackByDirection(direction);
        const canAttackPiece =
          selectedPiece.canAttackByDirection(direction) &&
          !!attackTarget &&
          (attackTarget === gamePiece ||
            (attackTarget.id === gamePiece.id &&
              attackTarget.location.x === gamePiece.location.x &&
              attackTarget.location.y === gamePiece.location.y));

        if (canAttackPiece) {
          void dispatchMoveOrAttack({
            action: GAME_ACTIONS.ATTACK,
            pieceId: selectedPiece.id,
            direction,
            gamePiece: {
              id: selectedPiece.id,
              value: selectedPiece.value,
              teamIndex: selectedPiece.teamIndex,
              location: { ...selectedPiece.location },
            },
            target: {
              id: gamePiece.id,
              value: gamePiece.value,
              teamIndex: gamePiece.teamIndex,
              location: { ...gamePiece.location },
            },
          });
          setSelectedPieceId(undefined);
          return;
        }

        if (selectedPiece.isInvalidMoveByDirectionDueToFollowing(direction)) {
          addSnackBar({
            title: 'WARNING',
            message: 'Cannot continue to follow the same piece.',
            variant: 'danger',
            timeout: 2000,
          });
          return;
        }

        return setSelectedPieceId(undefined);
      }

      if (gamePiece.isUnturned) {
        void dispatchFlip(gamePiece.id);
        return;
      }

      if (gamePiece.teamIndex === turnIndex) return setSelectedPieceId(gamePiece.id);
    },
    [
      displayGame,
      selectedPiece,
      inputLocked,
      dispatchMoveOrAttack,
      dispatchFlip,
      addSnackBar,
    ]
  );

  const handleLeave = useCallback(async () => {
    try {
      const casual =
        serverGame.gameMode === GAME_MODES.LOCAL_GAME ||
        serverGame.gameMode === GAME_MODES.AI_GAME;
      if (!casual) {
        await leaveGame();
      }
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  }, [leaveGame, navigate, serverGame.gameMode]);

  const handleReset = useCallback(async () => {
    clearActionIndicators();
    setResetStarted(true);
    try {
      if (!serverGame.isGameOver) {
        try {
          await leaveGameQuiet();
        } catch {
          // Slot may already be free if the match just ended.
        }
      }
      const created = (await createResetGame()) as { gameId?: string } | undefined;
      const nextId = created?.gameId;
      if (nextId) navigate(`/play/${nextId}`, { replace: true });
    } catch (err) {
      console.error(err);
    } finally {
      setResetStarted(false);
    }
  }, [
    createResetGame,
    leaveGameQuiet,
    navigate,
    serverGame.isGameOver,
    clearActionIndicators,
  ]);

  useEffect(() => {
    if (!serverGame.isGameOver) return;
    if (!gameId || !playerIsInGameId(userData, gameId)) return;
    void queryUserData();
  }, [serverGame, userData, gameId, queryUserData]);

  useEffect(() => {
    const watchKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') setSelectedPieceId(undefined);
    };
    document.addEventListener('keydown', watchKeyDown);
    return () => document.removeEventListener('keydown', watchKeyDown);
  }, []);

  return {
    blindKingsGame: displayGame,
    canvasBoard,
    authoritativeGame: serverGame,
    rewards,
    leaving,
    selectedPiece,
    showLeaveGameModal,
    renderVertically,
    boardAreaRef,
    setShowLeaveGameModal,
    handleLeave,
    handleReset,
    canReset,
    resetting,
    selectPiece,
    skinId,
    pendingFlipPieceId,
    localAction: playRequest,
    remoteFocus,
    onPlayComplete,
    inputLocked,
  };
}
